import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const SelectiveDisclosurePanel = ({ settings, onSettingsChange }) => {
  const [expandedContexts, setExpandedContexts] = useState({});

  const contextOptions = [
    { value: 'employment', label: 'Employment Verification' },
    { value: 'education', label: 'Educational Verification' },
    { value: 'healthcare', label: 'Healthcare Access' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'government', label: 'Government Services' },
    { value: 'travel', label: 'Travel & Immigration' }
  ];

  const attributeCategories = [
    {
      id: 'personal',
      name: 'Personal Information',
      attributes: [
        { id: 'fullName', label: 'Full Name', required: true },
        { id: 'dateOfBirth', label: 'Date of Birth', required: false },
        { id: 'nationality', label: 'Nationality', required: false },
        { id: 'address', label: 'Address', required: false },
        { id: 'phoneNumber', label: 'Phone Number', required: false },
        { id: 'email', label: 'Email Address', required: false }
      ]
    },
    {
      id: 'professional',
      name: 'Professional Credentials',
      attributes: [
        { id: 'jobTitle', label: 'Job Title', required: false },
        { id: 'employer', label: 'Current Employer', required: false },
        { id: 'experience', label: 'Years of Experience', required: false },
        { id: 'skills', label: 'Professional Skills', required: false },
        { id: 'certifications', label: 'Certifications', required: false },
        { id: 'licenses', label: 'Professional Licenses', required: false }
      ]
    },
    {
      id: 'educational',
      name: 'Educational Records',
      attributes: [
        { id: 'degree', label: 'Highest Degree', required: false },
        { id: 'institution', label: 'Educational Institution', required: false },
        { id: 'graduationYear', label: 'Graduation Year', required: false },
        { id: 'gpa', label: 'GPA/Grade', required: false },
        { id: 'transcripts', label: 'Academic Transcripts', required: false }
      ]
    }
  ];

  const toggleContextExpansion = (contextId) => {
    setExpandedContexts(prev => ({
      ...prev,
      [contextId]: !prev?.[contextId]
    }));
  };

  const handleAttributeToggle = (contextId, attributeId, enabled) => {
    const updatedSettings = {
      ...settings,
      selectiveDisclosure: {
        ...settings?.selectiveDisclosure,
        contexts: {
          ...settings?.selectiveDisclosure?.contexts,
          [contextId]: {
            ...settings?.selectiveDisclosure?.contexts?.[contextId],
            allowedAttributes: enabled
              ? [...(settings?.selectiveDisclosure?.contexts?.[contextId]?.allowedAttributes || []), attributeId]
              : (settings?.selectiveDisclosure?.contexts?.[contextId]?.allowedAttributes || [])?.filter(id => id !== attributeId)
          }
        }
      }
    };
    onSettingsChange(updatedSettings);
  };

  const handleContextPermissionChange = (contextId, permission) => {
    const updatedSettings = {
      ...settings,
      selectiveDisclosure: {
        ...settings?.selectiveDisclosure,
        contexts: {
          ...settings?.selectiveDisclosure?.contexts,
          [contextId]: {
            ...settings?.selectiveDisclosure?.contexts?.[contextId],
            permission: permission
          }
        }
      }
    };
    onSettingsChange(updatedSettings);
  };

  const getPermissionOptions = () => [
    { value: 'always', label: 'Always Allow' },
    { value: 'prompt', label: 'Prompt Each Time' },
    { value: 'never', label: 'Never Allow' }
  ];

  const isAttributeEnabled = (contextId, attributeId) => {
    return settings?.selectiveDisclosure?.contexts?.[contextId]?.allowedAttributes?.includes(attributeId) || false;
  };

  const getContextPermission = (contextId) => {
    return settings?.selectiveDisclosure?.contexts?.[contextId]?.permission || 'prompt';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Selective Disclosure Controls</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure which credential attributes can be shared in different verification contexts
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">Privacy Protected</span>
        </div>
      </div>
      <div className="space-y-4">
        {contextOptions?.map((context) => {
          const isExpanded = expandedContexts?.[context?.value];
          const contextPermission = getContextPermission(context?.value);
          
          return (
            <div key={context?.value} className="border border-border rounded-lg">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleContextExpansion(context?.value)}
                      className="w-8 h-8"
                    >
                      <Icon 
                        name={isExpanded ? "ChevronDown" : "ChevronRight"} 
                        size={16} 
                      />
                    </Button>
                    <div>
                      <h4 className="font-medium text-foreground">{context?.label}</h4>
                      <p className="text-xs text-muted-foreground">
                        {isExpanded ? 'Configure attribute sharing' : 'Click to configure'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-32">
                      <Select
                        options={getPermissionOptions()}
                        value={contextPermission}
                        onChange={(value) => handleContextPermissionChange(context?.value, value)}
                        placeholder="Permission"
                      />
                    </div>
                    <div className={`w-3 h-3 rounded-full ${
                      contextPermission === 'always' ? 'bg-success' :
                      contextPermission === 'prompt' ? 'bg-warning' : 'bg-error'
                    }`} />
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-4 pl-11 space-y-4">
                    {attributeCategories?.map((category) => (
                      <div key={category?.id} className="bg-muted/50 rounded-lg p-4">
                        <h5 className="font-medium text-sm text-foreground mb-3">
                          {category?.name}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {category?.attributes?.map((attribute) => (
                            <div key={attribute?.id} className="flex items-center justify-between">
                              <Checkbox
                                label={attribute?.label}
                                checked={isAttributeEnabled(context?.value, attribute?.id)}
                                onChange={(e) => handleAttributeToggle(
                                  context?.value, 
                                  attribute?.id, 
                                  e?.target?.checked
                                )}
                                disabled={attribute?.required}
                              />
                              {attribute?.required && (
                                <span className="text-xs text-muted-foreground">Required</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium">Privacy Protection Active</p>
            <p className="text-muted-foreground mt-1">
              Zero-knowledge proofs ensure that only selected attributes are revealed during verification, 
              maintaining your privacy while proving credential authenticity.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectiveDisclosurePanel;